import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { DocumentManager } from './document-manager'
import { DataManager } from './data-manager'
import { SelectionManager } from './selection-manager'
import { ProcessingManager } from './processing-manager'
import { ModalManager } from './modal-manager'
import { OCRRenderer } from './ocr-renderer'
import { UIManager } from './ui-manager'
import { WorkflowNav } from '../components/workflow-nav'
import { Toast } from '../components/toast'
import { llmUsageManager } from './llm-usage-manager'

export class EventManager {
  // private apiService: ApiService // Not used directly
  private appState: AppState
  private documentManager: DocumentManager
  private dataManager: DataManager
  // private selectionManager: SelectionManager // Not used directly
  private processingManager: ProcessingManager
  private modalManager: ModalManager
  private ocrRenderer: OCRRenderer
  private uiManager: UIManager
  private workflowNav: WorkflowNav | null = null
  private toast: Toast
  private app: any = null // Will be set by App class

  constructor(
    _apiService: ApiService,
    appState: AppState,
    documentManager: DocumentManager,
    dataManager: DataManager,
    _selectionManager: SelectionManager,
    processingManager: ProcessingManager,
    modalManager: ModalManager,
    ocrRenderer: OCRRenderer,
    uiManager: UIManager,
    toast: Toast
  ) {
    // this.apiService = apiService // Not used directly
    this.appState = appState
    this.documentManager = documentManager
    this.dataManager = dataManager
    // this.selectionManager = selectionManager // Not used directly
    this.processingManager = processingManager
    this.modalManager = modalManager
    this.ocrRenderer = ocrRenderer
    this.uiManager = uiManager
    this.toast = toast
  }

  public setWorkflowNav(workflowNav: WorkflowNav): void {
    this.workflowNav = workflowNav
  }

  public setApp(app: any): void {
    console.log('🎯 EventManager.setApp() called with:', {
      app: !!app,
      hasRenderSelectionResults: !!(app && app.renderSelectionResults),
      appType: app?.constructor?.name
    })
    this.app = app
  }

  public setupAllEventListeners(): void {
    this.setupAppStateListeners()
    this.setupWindowEventListeners()
    this.setupDocumentEventListeners()
  }

  private setupAppStateListeners(): void {
    this.appState.subscribe('currentStep', (_step: number) => {
      // Dispatch event to trigger step content re-rendering
      window.dispatchEvent(new CustomEvent('render-step-content'))
    })

    this.appState.subscribe('currentProject', async (project: any) => {
      console.log('🔄 Project change event:', {
        newProjectId: project?.id,
        hasCoordinates: this.appState.getCoordinates().length > 0
      })

      // CRITICAL: Ensure all pending requests are completed before switching
      if (this.appState.hasPendingRequests()) {
        console.log('⏳ Waiting for pending requests to complete before project switch...')
        await this.appState.waitForPendingRequests()
      }

      // Clear any remaining requests to prevent cross-contamination
      this.appState.clearPendingRequests()

      // Reset LLM usage tracking for the new project
      if (project) {
        llmUsageManager.setProjectId(project.id)
      } else {
        llmUsageManager.resetProjectUsage()
      }

      // Reset state first to prevent cross-contamination
      await this.appState.setSelections([])
      this.appState.setDescriptionList(null)
      this.appState.setCoordinates([], true) // Clear coordinates before restoring, skip persistence
      this.workflowNav?.refresh()
      
      // Render step content after state is cleared
      // Dispatch event to trigger step content re-rendering
      window.dispatchEvent(new CustomEvent('project-changed', { detail: { project } }))

      if (project) {
        console.log(`🔄 Project switched to: ${project.name}. Reloading assets.`)
        try {
          await this.dataManager.loadProjectDataParallel(project)
          // Hide loading modal after data loading completes
          this.uiManager.hideLoadingModal()
          console.log(`✅ Project data loaded successfully: ${project.name}`)
        } catch (error) {
          console.error('❌ Failed to load project data:', error)
          this.uiManager.hideLoadingModal()
          this.toast.error('Project Load Error', `Failed to load data for ${project.name}`)
        }
      }
    })

    // Add error handling for app state errors
    this.appState.subscribe('error', (error: string) => {
      console.error('❌ App State Error:', error)
      this.toast.error('Application Error', error)
      this.dataManager.handleAppStateError(error)
    })

    // Add recovery handling
    this.appState.subscribe('recovery', (recovered: boolean) => {
      if (recovered) {
        console.log('✅ App State Recovered')
        this.toast.success('Recovery Complete', 'Application state has been restored')
      }
    })

    this.appState.subscribe('currentFile', (file: any) => {
      this.workflowNav?.refresh()
      if (file) {
        const currentStepName = this.workflowNav?.getStepName()
        if (currentStepName === 'snippets' || currentStepName === 'input-capture') {
          this.documentManager.showPDFViewer(file)
        }
      } else {
        const currentStepName = this.workflowNav?.getStepName()
        if ((currentStepName !== 'snippets' && currentStepName !== 'input-capture') || !this.appState.getCurrentProject()) {
          this.documentManager.hidePDFViewer()
        }
      }
    })

    this.appState.subscribe('advancedMode', (isAdvancedMode: boolean) => {
      console.log('🔄 Advanced mode changed:', isAdvancedMode ? 'Standard' : 'Classic')
      
      // Refresh workflow navigation to reflect the new mode
      this.workflowNav?.refresh()
      
      // Update UI elements that depend on mode
      this.uiManager.updateSelectionsGalleryVisibility(this.appState)
      this.uiManager.updateNextButtonVisibility(this.workflowNav, this.appState)
      this.uiManager.updateTextSnippetsReadyCounter(this.workflowNav, this.appState)
      
      // Trigger a re-render of the current step content to reflect mode changes
      window.dispatchEvent(new CustomEvent('render-step-content'))
    })

    this.appState.subscribe('selections', (selections: any) => {
      console.log('🎯 EventManager received selections event:', selections.length, 'selections')
      this.workflowNav?.refresh()
      this.uiManager.updateSelectionsGalleryVisibility(this.appState)
      this.uiManager.updateNextButtonVisibility(this.workflowNav, this.appState)
      this.uiManager.updateTextSnippetsReadyCounter(this.workflowNav, this.appState)
      
      const currentStep = this.appState.getCurrentStep()
      const isAdvancedMode = this.appState.getAdvancedMode()
      
      console.log('🔄 Current step:', currentStep, 'isAdvancedMode:', isAdvancedMode)
      
      // Update Input and Capture step UI elements when selections change (step 2 in both modes)
      if (currentStep === 2) {
        console.log('🔄 Selections changed in Input and Capture step, updating UI elements')
        // Only update specific UI elements, don't trigger full re-render
        this.updateInputCaptureStepUI(selections)
      }
      
      // Re-render formatting step content when selections change (step 3 in classic mode)
      if (currentStep === 3 && !isAdvancedMode) {
        console.log('🔄 Selections changed in formatting step, preserving edits and re-rendering OCR processing cards')
        // Preserve any pending text edits before re-rendering (unless we're reprocessing OCR)
        // This will be handled by the calling App class
        this.ocrRenderer.renderOCRProcessingCards(selections)
        // Re-setup event listeners after re-rendering cards
        // This will be handled by TextProcessingEventManager
      }
      
      // Note: Classic Mode no longer has a separate snippets step (step 3)
      // Selections are now handled in the unified Input & Capture step (step 2)
      
      // Update input step display when selections change in advanced mode
      // REMOVED: This was also causing infinite loops and rate limiting issues
    })

    // Handle graceful selection updates (for OCR completion, etc.)
    this.appState.subscribe('selectionUpdated', (event: any) => {
      console.log('🔄 EventManager received graceful selection update:', event.selectionId)
      
      // Only update the specific selection card, don't trigger full re-renders
      this.updateSpecificSelectionCard(event.selectionId, event.selection)
    })
  }

  /**
   * Update a specific selection card without triggering full re-renders
   */
  private updateSpecificSelectionCard(selectionId: string, selection: any): void {
    try {
      // Find the specific selection card in the DOM
      const selectionCard = document.querySelector(`[data-selection-id="${selectionId}"]`)
      if (selectionCard) {
        // Update only the extracted text content if it exists
        const extractedTextElement = selectionCard.querySelector('.extracted-text-content')
        if (extractedTextElement && selection.extracted_text) {
          extractedTextElement.textContent = selection.extracted_text
          console.log('🔄 Updated extracted text for selection card:', selectionId)
        }
        
        // Update OCR status indicator if it exists
        const ocrStatusElement = selectionCard.querySelector('.ocr-status')
        if (ocrStatusElement && selection.ocr_status) {
          // Update OCR status display
          if (selection.ocr_status === 'completed') {
            ocrStatusElement.innerHTML = '<span class="text-green-600">✅ OCR Complete</span>'
          } else if (selection.ocr_status === 'processing') {
            ocrStatusElement.innerHTML = '<span class="text-blue-600">🔄 Processing...</span>'
          } else if (selection.ocr_status === 'failed') {
            ocrStatusElement.innerHTML = '<span class="text-red-600">❌ OCR Failed</span>'
          }
        }
        
        // Update hidden OCR text section in Advanced Mode
        const hiddenTextSection = selectionCard.querySelector(`[data-selection-id="${selectionId}"]`)
        if (hiddenTextSection && selection.extracted_text) {
          const summaryElement = hiddenTextSection.querySelector('summary')
          const contentElement = hiddenTextSection.querySelector('.mt-2')
          
          if (summaryElement) {
            summaryElement.textContent = `📝 View Extracted Text (${selection.extracted_text.length} chars)`
          }
          if (contentElement) {
            contentElement.textContent = selection.extracted_text
          }
          console.log('🔄 Updated hidden OCR text section for selection:', selectionId)
        }
        
        console.log('🔄 Gracefully updated selection card:', selectionId)
      } else {
        console.log('⚠️ Selection card not found for graceful update:', selectionId)
      }
    } catch (error) {
      console.error('❌ Failed to update specific selection card:', error)
    }
  }

  /**
   * Update Input and Capture step UI elements without full re-render
   */
  private updateInputCaptureStepUI(selections: any[]): void {
    console.log('🎯 updateInputCaptureStepUI called with', selections.length, 'selections')
    const hasSelections = selections.length > 0
    
    // Check if we're currently showing the landing page AND no results are already visible
    const landingContainer = document.querySelector('.input-capture-landing')
    const resultsContainer = document.querySelector('.selection-results')
    console.log('🔍 Landing container found:', !!landingContainer, 'Results container found:', !!resultsContainer, 'hasSelections:', hasSelections, 'app available:', !!this.app)
    
    // Only replace landing page if we have selections AND no results are already showing
    if (landingContainer && !resultsContainer && hasSelections && this.app) {
      // Replace the landing page with the selection results view
      const container = landingContainer.parentElement
      if (container) {
        console.log('🔄 Replacing landing page with selection results')
        this.app.renderSelectionResults(container)
      }
    }
    
    // Check if we're already showing selection results and need to update them
    console.log('🔍 Results container found:', !!resultsContainer, 'app available:', !!this.app)
    if (resultsContainer && this.app) {
      // Update the existing selection results display
      console.log('🔄 Updating existing selection results display')
      this.app.renderSelectionResults(resultsContainer.parentElement as HTMLElement)
    }
    
    // Also check for any individual selection cards that need updating
    const selectionCards = document.querySelectorAll('.selection-result-item')
    selectionCards.forEach(card => {
      const selectionId = card.getAttribute('data-selection-id')
      if (selectionId) {
        const selection = selections.find(s => s.id === selectionId)
        if (selection) {
          // Update the individual card content
          this.updateSelectionCard(card as HTMLElement, selection)
        }
      }
    })
  }
  
  /**
   * Update individual selection card content
   */
  private updateSelectionCard(card: HTMLElement, selection: any): void {
    // Find the content container within the card
    const contentContainer = card.querySelector('.selection-result-content')
    if (contentContainer && this.app) {
      // Update the preview content
      const newPreview = this.app.renderSelectionPreview(selection)
      console.log('🔄 Updating selection card:', {
        selectionId: selection.id,
        documentType: selection.document_type,
        isManual: selection.document_type === 'manual',
        newPreviewLength: newPreview.length,
        contentContainer: !!contentContainer
      })
      contentContainer.innerHTML = newPreview
      
      // Debug: Check what was actually inserted
      setTimeout(() => {
        const insertedContent = contentContainer.innerHTML
        const hasGreenBg = insertedContent.includes('bg-green-50')
        const hasManualText = insertedContent.includes('Manual Text Entry')
        console.log('🔍 DOM Update Debug:', {
          selectionId: selection.id,
          hasGreenBg: hasGreenBg,
          hasManualText: hasManualText,
          insertedLength: insertedContent.length,
          firstChars: insertedContent.substring(0, 100)
        })
      }, 100)
    }
  }

  private setupWindowEventListeners(): void {
    // Handle PDF auto-display after upload
    window.addEventListener('pdf-auto-display-requested', async (event: any) => {
      const { pdfFile } = event.detail
      console.log('🎬 PDF auto-display requested for:', pdfFile.filename)
      
      try {
        // Check if modal containers exist
        const viewerContainer = document.querySelector('#modal-pdf-viewer')
        const galleryContainer = document.querySelector('#modal-selections-gallery')
        
        console.log('🔍 Modal containers check:', {
          viewer: !!viewerContainer,
          gallery: !!galleryContainer,
          viewerElement: viewerContainer,
          galleryElement: galleryContainer
        })
        
        if (!viewerContainer) {
          console.warn('⚠️ Modal PDF viewer container not found, waiting for modal to be ready...')
          // Wait a bit for the modal to be fully rendered
          await new Promise(resolve => setTimeout(resolve, 500))
        }
        
        await this.modalManager.initializeModalPDFViewer(pdfFile)
        console.log('✅ PDF auto-display completed for:', pdfFile.filename)
      } catch (error) {
        console.error('❌ Failed to auto-display PDF:', error)
        this.toast.error('PDF Display Error', `Failed to display ${pdfFile.filename}`)
      }
    })

    // Handle document auto-display request (for images and other documents)
    window.addEventListener('document-auto-display-requested', async (event: any) => {
      const { document: documentData } = event.detail
      console.log('🎬 Document auto-display requested for:', documentData.original_filename, 'Type:', documentData.document_type)
      
      try {
        // Check if modal containers exist
        const viewerContainer = document.querySelector('#modal-pdf-viewer')
        const galleryContainer = document.querySelector('#modal-selections-gallery')
        
        console.log('🔍 Modal containers check:', {
          viewer: !!viewerContainer,
          gallery: !!galleryContainer,
          viewerElement: viewerContainer,
          galleryElement: galleryContainer
        })
        
        if (!viewerContainer) {
          console.warn('⚠️ Modal viewer container not found, waiting for modal to be ready...')
          // Wait a bit for the modal to be fully rendered
          await new Promise(resolve => setTimeout(resolve, 500))
        }
        
        // Route to appropriate viewer based on document type
        if (documentData.document_type === 'image') {
          await this.modalManager.initializeModalImageViewer(documentData)
        } else {
          // For other document types, we could add more handlers here
          console.warn('⚠️ Unsupported document type for auto-display:', documentData.document_type)
        }
        
        console.log('✅ Document auto-display completed for:', documentData.original_filename)
      } catch (error) {
        console.error('❌ Failed to auto-display document:', error)
        this.toast.error('Document Display Error', `Failed to display ${documentData.original_filename}`)
      }
    })

    // Handle generate list request
    window.addEventListener('generate-list-requested', async () => {
      console.log('📝 Generate list requested - event listener triggered!')
      
      // Show loading state on button
      const generateBtn = document.getElementById('generate-description-list-btn') as HTMLButtonElement
      console.log('🔍 Generate button in event listener:', !!generateBtn, generateBtn)
      
      if (generateBtn) {
        const originalText = generateBtn.innerHTML
        generateBtn.innerHTML = '⏳ Generating...'
        generateBtn.disabled = true
        
        try {
          console.log('🚀 Calling generateNumberedList...')
          await this.processingManager.generateNumberedList()
          console.log('✅ Description list generated successfully')
        } catch (error) {
          console.error('❌ Failed to generate description list:', error)
          this.toast.error('Generation Error', 'Failed to generate description list')
        } finally {
          // Restore button state
          generateBtn.innerHTML = originalText
          generateBtn.disabled = false
        }
      } else {
        console.error('❌ Generate button not found in event listener!')
      }
    })

    // Handle convert coordinates request
    window.addEventListener('convert-coordinates-requested', async () => {
      console.log('🎯 Convert coordinates requested - event listener triggered!')
      
      // Show loading state on button
      const convertBtn = document.getElementById('convert-to-coords-btn') as HTMLButtonElement
      console.log('🔍 Convert button in event listener:', !!convertBtn, convertBtn)
      
      if (convertBtn) {
        const originalText = convertBtn.innerHTML
        convertBtn.innerHTML = '⏳ Converting...'
        convertBtn.disabled = true
        
        try {
          console.log('🚀 Calling convertListToCoordinates...')
          await this.processingManager.convertListToCoordinates()
          console.log('✅ Coordinates converted successfully')
        } catch (error) {
          console.error('❌ Failed to convert coordinates:', error)
          this.toast.error('Conversion Error', 'Failed to convert descriptions to coordinates')
        } finally {
          // Restore button state
          convertBtn.innerHTML = originalText
          convertBtn.disabled = false
        }
      } else {
        console.error('❌ Convert button not found in event listener!')
      }
    })

    // Handle navigation to specific step
    window.addEventListener('navigate-to-step', (event: any) => {
      const step = event.detail?.step
      if (step && this.workflowNav) {
        console.log(`🎯 Navigating to step ${step}`)
        this.workflowNav.navigateToStep(step)
      }
    })

    // Handle modal close events to re-render step content
    window.addEventListener('modal-closed', (event: any) => {
      const { type } = event.detail
      console.log('🔄 Modal closed, re-rendering step content:', type)
      // Dispatch event to trigger step content re-rendering
      window.dispatchEvent(new CustomEvent('render-step-content'))
    })

    // Handle project change events to re-render step content
    window.addEventListener('project-changed', (event: any) => {
      const { project } = event.detail
      console.log('🔄 Project changed, re-rendering step content:', project?.name)
      // Dispatch event to trigger step content re-rendering
      window.dispatchEvent(new CustomEvent('render-step-content'))
    })
  }

  private setupDocumentEventListeners(): void {
    document.addEventListener('keydown', (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'o':
            event.preventDefault()
            // File upload will be handled by the calling App class
            break
          case 'ArrowLeft':
            if (this.workflowNav) {
              const currentStep = this.workflowNav.getCurrentStep()
              if (currentStep > 1) {
                this.workflowNav.navigateToStep(currentStep - 1)
              }
            }
            break
          case 'ArrowRight':
            if (this.workflowNav) {
              const currentStep = this.workflowNav.getCurrentStep()
              const nextStep = currentStep + 1
              
              // Check if we're navigating to Descriptions step (step 3) in Classic Mode
              if (nextStep === 3 && !this.appState.getAdvancedMode()) {
                this.handleNavigationToDescriptionsStep()
              } else if (this.workflowNav.canAdvanceToStep(nextStep)) {
                this.workflowNav.navigateToStep(nextStep)
              }
            }
            break
        }
      }
    })

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (target.id === 'step-prev-bottom') {
        if (this.workflowNav) {
          const currentStep = this.workflowNav.getCurrentStep()
          if (currentStep > 1) {
            this.workflowNav.navigateToStep(currentStep - 1)
          }
        }
      }
      if (target.id === 'step-next-bottom' || target.id === 'step-next-top') {
        if (this.workflowNav) {
          const currentStep = this.workflowNav.getCurrentStep()
          const nextStep = currentStep + 1
          
          // Check if we're navigating to Descriptions step (step 3) in Classic Mode
          if (nextStep === 3 && !this.appState.getAdvancedMode()) {
            this.handleNavigationToDescriptionsStep()
          } else if (this.workflowNav.canAdvanceToStep(nextStep)) {
            this.workflowNav.navigateToStep(nextStep)
          }
        }
      }
    })
  }

  /**
   * Handle navigation to Descriptions step with OCR completion check
   */
  private async handleNavigationToDescriptionsStep(): Promise<void> {
    const selections = this.appState.getSelections()
    
    if (selections.length === 0) {
      this.toast.warning('No Selections', 'Please make text selections first')
      return
    }

    // Check if all selections have been processed (OCR complete)
    const unprocessedSelections = selections.filter(sel => !sel.processed)
    
    if (unprocessedSelections.length > 0) {
      console.log('⏳ OCR still processing for selections:', unprocessedSelections.length)
      this.toast.info('Processing Selections', `OCR is still processing ${unprocessedSelections.length} selection(s). Please wait...`)
      
      // Show loading modal while waiting for OCR completion
      this.uiManager.updateLoadingModal('Processing Selections...', `OCR is processing ${unprocessedSelections.length} selection(s)`)
      
      // Wait for OCR completion
      await this.waitForOCRCompletion()
      
      // Hide loading modal
      this.uiManager.hideLoadingModal()
    }
    
    // Now navigate to Descriptions step
    if (this.workflowNav) {
      this.workflowNav.navigateToStep(3)
    }
  }

  /**
   * Wait for OCR processing to complete for all selections
   */
  private async waitForOCRCompletion(): Promise<void> {
    const maxWaitTime = 30000 // 30 seconds max wait
    const checkInterval = 1000 // Check every second
    let waitTime = 0
    
    while (waitTime < maxWaitTime) {
      const selections = this.appState.getSelections()
      const unprocessedSelections = selections.filter(sel => !sel.processed)
      
      if (unprocessedSelections.length === 0) {
        console.log('✅ OCR processing completed for all selections')
        return
      }
      
      console.log(`⏳ Still waiting for OCR completion: ${unprocessedSelections.length} selections remaining`)
      
      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, checkInterval))
      waitTime += checkInterval
    }
    
    // If we get here, OCR took too long
    console.warn('⚠️ OCR processing timeout - proceeding anyway')
    this.toast.warning('Processing Timeout', 'OCR processing is taking longer than expected. Proceeding to Descriptions step.')
  }

  public setupLandingEventListeners(): void {
    const uploadOption = document.getElementById('choose-upload-documents')
    const manualOption = document.getElementById('choose-manual-entry')
    
    uploadOption?.addEventListener('click', () => {
      this.modalManager.openDocumentUploadModal()
    })
    
    manualOption?.addEventListener('click', () => {
      this.modalManager.openManualEntryModal()
    })
  }

  public setupSelectionResultsEventListeners(): void {
    const addMoreBtn = document.getElementById('add-more-selections')
    const processBtn = document.getElementById('process-selections')
    const convertBtn = document.getElementById('convert-to-coords-btn')
    
    addMoreBtn?.addEventListener('click', () => {
      this.modalManager.openDocumentUploadModal()
    })
    
    if (processBtn) {
      processBtn.addEventListener('click', async (e) => {
        e.preventDefault()
        this.uiManager.showLoadingModal('Processing Your Descriptions', 'OCR and AI processing happening in the background...')
        this.processingManager.startBackgroundProcessing()
      })
    }
    
    if (convertBtn) {
      convertBtn.addEventListener('click', (e) => {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('convert-coordinates-requested'))
      })
    }
  }

  public setupTextareaEventListeners(): void {
    // Attach coordinate textarea listener with auto-resize
    const coordTextarea = document.getElementById('coordinate-list-textarea') as HTMLTextAreaElement
    if (coordTextarea && !coordTextarea.dataset.listenerAttached) {
      coordTextarea.addEventListener('input', () => {
        this.uiManager.autoResizeTextarea(coordTextarea)
      })
      coordTextarea.dataset.listenerAttached = 'true'
    }

    // Attach description textarea listener with auto-resize
    const descTextarea = document.getElementById('description-list-textarea') as HTMLTextAreaElement
    if (descTextarea && !descTextarea.dataset.listenerAttached) {
      descTextarea.addEventListener('input', () => {
        this.uiManager.autoResizeTextarea(descTextarea)
      })
      descTextarea.dataset.listenerAttached = 'true'
      
      descTextarea.addEventListener('blur', () => {
        this.dataManager.saveDescriptionListToDatabase(descTextarea.value)
      })
    }
  }
}
